import React, { RefObject, useCallback, useEffect, useState } from 'react';
import styles from './ResourceCollaboration.module.scss';
import { Tabs } from 'new/elements/Tabs';
import { ShareAction } from 'new/elements/ResourceCollaboration/ShareAction';
import { Table } from 'new/elements/Table';
import { aclColumns } from 'new/components/collaborate/ResourceDetail/aclColumns';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { useResourceAclItems } from 'new/hooks/adapters/useResourceAclItems';
import { aclTypes } from 'new/components/collaborate/ResourceDetail/ResourceDetail';
import { fetchAcl, fetchOrgMembers, loadMyTeams } from 'js/actions';
import { useDispatch } from 'react-redux';
import { PillTabs } from 'new/elements/PillTabs';
import cx from 'classnames';

interface ResourceCollaborationProps {
  resourceType: string;
  resourceId: string;
  parentScrollRef?: RefObject<HTMLElement>;
  height?: number;
  usePillTabs?: boolean;
}

const tabs = [
  {
    label: 'Users shared with',
    value: 'users',
  },
  {
    label: 'Teams shared with',
    value: 'teams',
  },
  {
    label: 'Companies shared with',
    value: 'orgs',
  },
];

export const ResourceCollaboration = (props: ResourceCollaborationProps) => {
  const { resourceType: type, resourceId: id, parentScrollRef, height, usePillTabs } = props;
  const [selected, setSelected] = useState([]);

  const org = useCurrentOrg();

  const { resource } = useResource(type, id);
  const dispatch = useDispatch();
  const aclType = aclTypes[type];
  const { aclItems, owner } = useResourceAclItems(resource, aclType);

  const [tab, setTab] = useState('users');
  const [items, setItems] = useState(filterItems());

  function filterItems() {
    if (tab === 'users') {
      return [
        owner,
        ...aclItems.filter((i) => {
          return i.type === 'USER';
        }),
      ];
    }
    if (tab === 'teams') {
      return aclItems.filter((i) => {
        return i.type === 'TEAM';
      });
    }
    return aclItems.filter((i) => {
      return i.type === 'ORG';
    });
  }

  useEffect(() => {
    if (aclType) {
      dispatch(fetchOrgMembers(org.id));
      dispatch(loadMyTeams());
      dispatch(fetchAcl(aclType, id));
    }
  }, []);

  useEffect(() => {
    setItems(filterItems());
  }, [tab, aclItems, owner]);

  const handleTabChange = (e, value) => {
    if (typeof e === 'string') {
      setTab(e);
    } else {
      setTab(value);
    }
  };

  const onSelectItem = (items) => {
    setSelected(items);
  };

  const isRowSelectable = useCallback((row) => row.id !== owner.id, [owner]);

  return (
    <div>
      <div className={cx(styles.tabsContainer, usePillTabs ? styles.pillTabsContainer : '')}>
        {props.usePillTabs ? (
          <PillTabs
            tabs={tabs}
            // @ts-ignore
            onSelectionChange={handleTabChange}
            selected={tab}
          />
        ) : (
          <Tabs tabs={tabs} value={tab} onChange={handleTabChange} />
        )}
      </div>
      <ShareAction
        data={resource}
        externalSelection={selected}
        aclType={aclType}
        resourceId={id}
        alreadyShared={items}
      />
      <Table
        onSelectedRowsChange={onSelectItem}
        isRowSelectable={isRowSelectable}
        parentScrollRef={parentScrollRef}
        height={height}
        columns={aclColumns[tab]}
        rows={items}
      />
    </div>
  );
};
